import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, Lock, FileText, Phone, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import Seo from '../components/Seo';

const HIPAANotice = () => {
  return (
    <div className="min-h-screen pt-20 bg-gray-50">
      <Seo
        title="HIPAA Notice of Privacy Practices | MoveraCare Mobility"
        description="HIPAA Notice of Privacy Practices for MoveraCare Mobility describing how Protected Health Information (PHI) may be used and disclosed."
        path="/hipaa"
        noindex
      />
      {/* Header */}
      <section className="bg-[#004C97] py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center text-white hover:text-[#F9A825] transition-colors mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <div className="flex items-center space-x-4 mb-4">
            <ShieldCheck className="w-12 h-12 text-[#F9A825]" />
            <h1 className="text-3xl md:text-4xl font-bold text-white">HIPAA Notice of Privacy Practices</h1>
          </div>
          <p className="text-gray-200">
            <strong>Effective Date:</strong> April 25, 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Important Notice */}
        <div className="bg-[#F9A825] border-4 border-[#004C97] rounded-lg p-6 mb-8">
          <h2 className="text-2xl font-bold text-[#004C97] mb-3">IMPORTANT NOTICE</h2>
          <p className="text-[#004C97] text-lg font-medium">
            THIS NOTICE DESCRIBES HOW MEDICAL INFORMATION ABOUT YOU MAY BE USED AND DISCLOSED AND HOW YOU CAN GET ACCESS TO THIS INFORMATION. PLEASE REVIEW IT CAREFULLY.
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-8 space-y-8">
          <div>
            <h2 className="text-2xl font-bold text-[#004C97] mb-4">Our Legal Duty</h2>
            <p className="text-gray-700 leading-relaxed">
              MoveraCare Services LLC is required by federal and state law to maintain the privacy of your Protected Health Information (PHI) and to provide you with this notice of our legal duties and privacy practices with respect to your PHI. This notice describes how we may use and disclose your PHI in accordance with applicable law, including the Health Insurance Portability and Accountability Act (HIPAA).
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-[#004C97] mb-4 flex items-center">
              <FileText className="w-6 h-6 mr-2 text-[#F9A825]" />
              1. Uses and Disclosures for Treatment, Payment, and Operations
            </h2>
            
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-[#F9A825]">
                <h3 className="font-semibold text-[#004C97] mb-2">Treatment</h3>
                <p className="text-gray-700">
                  We may share your PHI with your doctor, dialysis center, or hospital to coordinate your transportation and ensure proper care continuity. For example, we may inform your dialysis center of your estimated arrival time.
                </p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-[#F9A825]">
                <h3 className="font-semibold text-[#004C97] mb-2">Payment</h3>
                <p className="text-gray-700">
                  We may share your PHI with insurance brokers (e.g., MTM, Modivcare, LogistiCare) and Medicaid/Medicare to receive payment for transportation services. This may include your name, appointment details, and mobility requirements.
                </p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-[#F9A825]">
                <h3 className="font-semibold text-[#004C97] mb-2">Healthcare Operations</h3>
                <p className="text-gray-700">
                  We may use your PHI for quality improvement, driver training, and compliance audits. For example, we may review trip records to improve our on-time performance.
                </p>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-[#004C97] mb-4 flex items-center">
              <Lock className="w-6 h-6 mr-2 text-[#F9A825]" />
              2. Minimum Necessary Standard
            </h2>
            <p className="text-gray-700">
              We follow the "minimum necessary" rule, meaning we only access, use, or share the least amount of your PHI needed to accomplish the intended purpose. Only authorized personnel have access to your transportation records.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-[#004C97] mb-4">3. Your Privacy Rights</h2>
            <p className="text-gray-700 mb-4">You have the following rights regarding your PHI:</p>
            
            <ul className="space-y-3">
              <li className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-[#004C97] mb-2">Right to Access</h3>
                <p className="text-gray-700">You have the right to request and receive a copy of your transportation records.</p>
              </li>
              
              <li className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-[#004C97] mb-2">Right to Amend</h3>
                <p className="text-gray-700">You may request corrections to inaccurate or incomplete information in your records.</p>
              </li>
              
              <li className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-[#004C97] mb-2">Right to Accounting of Disclosures</h3>
                <p className="text-gray-700">You may request a list of certain disclosures we have made of your PHI.</p>
              </li>
              
              <li className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-[#004C97] mb-2">Right to Request Restrictions</h3>
                <p className="text-gray-700">You may request that we limit how we use or disclose your PHI. We are not required to agree to your request, but if we do, we will comply with the restriction.</p>
              </li>
              
              <li className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-[#004C97] mb-2">Right to Confidential Communications</h3>
                <p className="text-gray-700">You may request that we communicate with you at a specific address or phone number.</p>
              </li>
              
              <li className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-[#004C97] mb-2">Right to Breach Notification</h3>
                <p className="text-gray-700">You will be notified promptly if there is a breach of your unsecured PHI.</p>
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-[#004C97] mb-4">4. Permitted Disclosures Without Authorization</h2>
            <p className="text-gray-700 mb-4">
              In certain limited situations, we may disclose your PHI without your written authorization:
            </p>
            <ul className="space-y-2 ml-6">
              <li className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-[#F9A825] rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-700">When required by law (e.g., court order, subpoena)</span>
              </li>
              <li className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-[#F9A825] rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-700">Public health and safety activities</span>
              </li>
              <li className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-[#F9A825] rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-700">Law enforcement purposes (with proper legal authority)</span>
              </li>
              <li className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-[#F9A825] rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-700">To avert a serious threat to health or safety</span>
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-[#004C97] mb-4">5. Our Responsibilities</h2>
            <ul className="space-y-2 ml-6">
              <li className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-[#F9A825] rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-700">Maintain the privacy and security of your PHI</span>
              </li>
              <li className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-[#F9A825] rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-700">Provide you with this Notice of Privacy Practices</span>
              </li>
              <li className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-[#F9A825] rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-700">Follow the terms of the Notice currently in effect</span>
              </li>
              <li className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-[#F9A825] rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-700">Notify you of any breach of your unsecured PHI</span>
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-[#004C97] mb-4">6. HIPAA Compliance Officer</h2>
            <p className="text-gray-700 mb-4">
              For any privacy concerns, to exercise your rights, or to file a complaint, please contact our HIPAA Compliance Officer:
            </p>
            <div className="bg-[#F9A825]/10 border-2 border-[#F9A825] p-6 rounded-lg">
              <p className="font-semibold text-[#004C97] text-lg mb-3">HIPAA Compliance Officer</p>
              <p className="text-gray-700 font-medium mb-4">MoveraCare Services LLC</p>
              <div className="space-y-2">
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-[#F9A825]" />
                  <span className="text-gray-700"><strong>Phone:</strong> (832) 925-2128</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-[#F9A825]" />
                  <span className="text-gray-700"><strong>Email:</strong> hipaa@moveracaremobility.com</span>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="text-gray-700"><strong>Fax:</strong> (713) 583-2334</span>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-[#004C97] mb-4">7. Filing a Complaint</h2>
            <p className="text-gray-700 mb-4">
              If you believe your privacy rights have been violated, you may file a complaint with:
            </p>
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-semibold text-[#004C97]">MoveraCare Compliance Officer (above)</p>
                <p className="text-sm text-gray-600 mt-1">Internal complaint process</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-semibold text-[#004C97]">U.S. Department of Health and Human Services</p>
                <p className="text-gray-700">Office for Civil Rights</p>
                <p className="text-sm text-gray-600 mt-1">Website: www.hhs.gov/ocr/privacy</p>
              </div>
            </div>
            <p className="text-gray-700 mt-4">
              <strong>You will not be retaliated against for filing a complaint.</strong>
            </p>
          </div>

          <div className="border-t pt-6">
            <p className="text-sm text-gray-600">
              <strong>Changes to This Notice:</strong> We reserve the right to change this Notice. Any revised Notice will apply to all PHI we maintain, including information created or received before the change. The current version will always be available on our website and at our office.
            </p>
          </div>
        </div>

        <div className="mt-8 text-center">
          <Link to="/">
            <Button className="bg-[#004C97] text-white hover:bg-[#003D7A]">
              Return to Homepage
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default HIPAANotice;